import * as React from 'react'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { StudioLayoutLink } from '@repo/studio-config/studio-config-router'
import { OptionRow } from '@repo/studio-design-system-ui-option-row'
import { PopOverContainer } from '@repo/studio-design-system-ui-pop-over-container'
import {
  getAppRendererOnline,
  subscribeAppRendererOnline,
} from '@repo/studio-utils/desktop/app-renderer-online'
import {
  hostWorkspaceHardNavigation,
  hostWorkspaceHomeRoute,
} from '@repo/studio-utils/desktop/workspace-home-route'
import { WorkspaceTrigger } from './workspace-trigger'
import { availableWorkspaces } from './workspaces'
import type { StudioWorkspace } from './workspaces'

export function WorkspaceSwitcher({
  current,
  homeRouteFor = hostWorkspaceHomeRoute,
  hardNavigationFor = hostWorkspaceHardNavigation,
}: {
  current: StudioWorkspace
  homeRouteFor?: (workspace: StudioWorkspace) => string
  hardNavigationFor?: (workspace: StudioWorkspace) => boolean
}) {
  const [open, setOpen] = React.useState(false)
  const t = useStudioTranslate()
  const workspaces = availableWorkspaces(current)
  const offline = useIsOffline()

  return (
    <PopOverContainer.Root open={open} onOpenChange={setOpen}>
      <PopOverContainer.Trigger>
        <WorkspaceTrigger open={open} aria-label={current.label}>
          {current.logo}
        </WorkspaceTrigger>
      </PopOverContainer.Trigger>
      <PopOverContainer.Content className="w-[388px] max-w-[calc(100vw-24px)] gap-1">
        {workspaces.map(workspace => (
          <WorkspaceOption
            key={workspace.id}
            workspace={workspace}
            homeRoute={homeRouteFor(workspace)}
            description={t(workspace.descriptionKey)}
            selected={workspace.id === current.id}
            unavailable={offline && workspace.id !== 'desktop'}
            reloadDocument={Boolean(hardNavigationFor(workspace) || hardNavigationFor(current))}
            onNavigate={() => setOpen(false)}
          />
        ))}
      </PopOverContainer.Content>
    </PopOverContainer.Root>
  )
}

/**
 * The local `app://` renderer reports `navigator.onLine` as false even with a
 * network, so it follows the app-renderer store instead. Everywhere else the
 * store is not installed and the browser flag still applies.
 */
function useIsOffline(): boolean {
  const appRendererOnline = React.useSyncExternalStore(
    subscribeAppRendererOnline,
    getAppRendererOnline,
    getAppRendererOnline,
  )
  if (appRendererOnline !== null) {
    return !appRendererOnline
  }
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

function WorkspaceOption({
  workspace,
  homeRoute,
  description,
  selected,
  unavailable,
  reloadDocument,
  onNavigate,
}: {
  workspace: StudioWorkspace
  homeRoute: string
  description: string
  selected: boolean
  unavailable: boolean
  reloadDocument: boolean
  onNavigate: () => void
}) {
  const t = useStudioTranslate()
  return (
    <OptionRow selected={selected} asChild className="h-auto gap-3 py-1.5">
      <StudioLayoutLink
        to={homeRoute}
        reloadDocument={reloadDocument}
        aria-current={selected ? 'page' : undefined}
        aria-disabled={unavailable || undefined}
        aria-label={workspace.label}
        onClick={event => {
          if (unavailable) {
            event.preventDefault()
            return
          }
          onNavigate()
        }}
      >
        <WorkspaceOptionBody
          workspace={workspace}
          description={unavailable ? t('studio.desktop.workspace_unavailable') : description}
        />
      </StudioLayoutLink>
    </OptionRow>
  )
}

function WorkspaceOptionBody({
  workspace,
  description,
}: {
  workspace: StudioWorkspace
  description: string
}) {
  return (
    <>
      <span className="flex w-6 shrink-0 items-center justify-center [&>svg]:h-auto [&>svg]:w-full">
        {workspace.visualMark}
      </span>
      <span className="flex min-w-px flex-1 flex-col items-start overflow-clip text-left">
        <span className="studio-typography-14 w-full truncate">{workspace.label}</span>
        <span className="studio-typography-12 w-full truncate text-theme_semantic_surface_ui_foreground-color-faint">
          {description}
        </span>
      </span>
    </>
  )
}
